import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Code,
  Settings,
  FileText,
  Copy,
  Check,
  Sun,
  Moon,
  Eye,
  ExternalLink,
  Loader2,
} from "lucide-react";
import {
  IoSunnyOutline,
  IoPartlySunnyOutline,
  IoSunny,
  IoPartlySunny,
  IoMoonOutline,
  IoMoon,
} from "react-icons/io5";
import AppLayout from "../components/Layout/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { auth } from "../config/firebase";
import { savePublicCalendarContext } from "@/lib/publicCalendarContext";

// Utility function to format time with AM/PM using prayer-specific logic
const formatTimeWithAMPM = (time, prayerName = null) => {
  if (!time) return "-";

  // If already has AM/PM, return as is
  if (/[AaPp][Mm]/.test(time)) return time;

  // Parse time (expecting HH:MM format)
  const [hours, minutes] = time.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time;

  let period;
  let displayHours = hours;

  // Apply prayer-specific rules
  if (prayerName === "fajr" || prayerName === "sunrise") {
    // Fajr and Sunrise are always AM
    period = "AM";
    displayHours = hours % 12 || 12;
  } else if (prayerName === "duhr" || prayerName === "jummah") {
    // Duhr and Jummah are AM only if between 10:00-11:59, otherwise PM
    if (hours >= 10 && hours < 12) {
      period = "AM";
      displayHours = hours;
    } else {
      period = "PM";
      displayHours = hours > 12 ? hours - 12 : hours;
    }
  } else if (
    prayerName === "asr" ||
    prayerName === "maghrib" ||
    prayerName === "isha"
  ) {
    // Asr, Maghrib, and Isha are always PM
    period = "PM";
    displayHours = hours > 12 ? hours - 12 : hours;
  } else {
    // Default fallback for times without prayer context
    period = hours >= 12 ? "PM" : "AM";
    displayHours = hours % 12 || 12;
  }

  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

const API_URL = import.meta.env.VITE_API_URL;
const API_BASE_URL = import.meta.env.VITE_API_URL;

// PreviewIframe component for widget preview
function PreviewIframe({ src, size = "medium", targetHeight }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [dimensions, setDimensions] = useState({ width: 400, height: 400 });

  useEffect(() => {
    const compute = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const heightMap = {
        small: 250,
        medium: 400,
        large: 500,
      };

      const rawHeight = targetHeight || heightMap[size] || heightMap.medium;
      const height = rawHeight;
      const width = Math.max(220, Math.round((rawHeight * 9) / 16));

      const sW = rect.width / width;
      const sH = rect.height / height;
      const s = Math.min(sW, sH);
      const clamped = Math.max(0.35, Math.min(s, 1));

      setScale(clamped);
      setDimensions({ width, height });
    };
    compute();
    const handle = () => compute();
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, [size, targetHeight]);

  if (size === "fullwidth") {
    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <iframe
          src={src}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
          }}
          title="Widget Preview"
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        overflow: "auto",
        position: "relative",
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: `${dimensions.width}px`,
          height: `${dimensions.height}px`,
          transform: `scale(${scale})`,
          transformOrigin: "top center",
          flexShrink: 0,
          margin: "0 auto",
          marginBottom: 0,
        }}
      >
        <iframe
          src={src}
          style={{
            width: `${dimensions.width}px`,
            height: `${dimensions.height}px`,
            border: "none",
            display: "block",
            pointerEvents: "auto",
            margin: 0,
            padding: 0,
            verticalAlign: "top",
          }}
          title="Widget Preview"
          scrolling="no"
        />
      </div>
    </div>
  );
}

export default function PrayerWidget() {
  const [searchParams] = useSearchParams();
  const { user, userProfile } = useAuth();
  const { appearance: userAppearance, theme: userTheme } = useTheme();
  const customizeMode = searchParams.get("customize") === "true";
  const token =
    searchParams.get("token") ??
    new URLSearchParams(window.location.search).get("token");
  const rawQuery = typeof window !== "undefined" ? window.location.search : "";

  // Use user's theme when in customize mode, otherwise use URL param
  const themeParamRaw = customizeMode
    ? userAppearance // Use user's theme preference in customize mode
    : searchParams.get("theme") ??
      new URLSearchParams(window.location.search).get("theme") ??
      "light";
  const themeParam = (themeParamRaw || "light").trim().toLowerCase();
  const theme = themeParam === "dark" ? "dark" : "light";

  // Use user's theme color when in customize mode, otherwise use URL param
  const colorParam = customizeMode
    ? userTheme?.colors?.primary?.replace("#", "") || "bf9a2f"
    : searchParams.get("color") || "bf9a2f";
  const accentColor = colorParam.startsWith("#")
    ? colorParam
    : `#${colorParam}`;

  const rawSizeParam = (searchParams.get("size") || "medium").toLowerCase();
  const sizeParam = ["small", "medium", "large", "custom"].includes(
    rawSizeParam
  )
    ? rawSizeParam
    : "medium";
  const heightParam = parseInt(searchParams.get("height") || "", 10);
  const HEIGHT_PRESETS = {
    small: 250,
    medium: 400,
    large: 500,
  };
  const normalizeHeight = (val, fallback) => {
    const num = Number(val);
    if (Number.isFinite(num) && num > 0) {
      return Math.max(200, num);
    }
    return fallback;
  };
  const resolveHeight = (sizeValue, customHeightValue) => {
    if (sizeValue === "custom") {
      return normalizeHeight(customHeightValue, HEIGHT_PRESETS.medium);
    }
    return HEIGHT_PRESETS[sizeValue] || HEIGHT_PRESETS.medium;
  };
  const targetHeight = resolveHeight(sizeParam, heightParam);

  // Customization state (only used in customize mode)
  const [widgetToken, setWidgetToken] = useState(null);
  const [copiedWidget, setCopiedWidget] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [widgetTheme, setWidgetTheme] = useState("light");
  const [widgetColor, setWidgetColor] = useState("#bf9a2f");
  const [widgetSize, setWidgetSize] = useState(sizeParam || "medium");
  const [widgetHeight, setWidgetHeight] = useState(
    resolveHeight(sizeParam, heightParam)
  );
  const [fitScale, setFitScale] = useState(1);

  // Initialize and update widget theme and color from user's theme preference (in customize mode)
  useEffect(() => {
    if (customizeMode) {
      setWidgetTheme(userAppearance || "light");
      if (userTheme?.colors?.primary) {
        setWidgetColor(userTheme.colors.primary);
      }
    }
  }, [customizeMode, userAppearance, userTheme]);

  // Convert hex color to rgba string with given alpha
  const hexToRgba = (hex, alpha = 1) => {
    const normalized = (hex || "").replace("#", "");
    if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalized)) return hex || "rgba(0,0,0,0)";
    const full = normalized.length === 3
      ? normalized.split("").map((c) => c + c).join("")
      : normalized;
    const intVals = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
    return `rgba(${intVals[0]}, ${intVals[1]}, ${intVals[2]}, ${alpha})`;
  };

  const [masjidData, setMasjidData] = useState(null);
  const [prayerTimes, setPrayerTimes] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEmbedded, setIsEmbedded] = useState(false);
  const containerRef = useRef(null);
  const cardRef = useRef(null);

  // Determine if widget should render as embedded (no extra space, exact height)
  // This is true when: actually in iframe OR viewing directly with token (not in customize mode)
  const shouldRenderAsEmbedded = isEmbedded || (token && !customizeMode);

  // Make widget responsive when embedded - use CSS scaling instead of transform
  useEffect(() => {
    if (!isEmbedded || !cardRef.current) return;

    // Remove any transform scaling - let CSS handle it naturally
    cardRef.current.style.transform = "none";
    cardRef.current.style.transformOrigin = "unset";
  }, [isEmbedded]);

  useEffect(() => {
    // Check if we're embedded in an iframe
    const inIframe = window.self !== window.top;
    setIsEmbedded(inIframe);

    // If embedded in iframe OR viewing widget directly (has token, not in customize mode)
    // Set up page to show only widget without website chrome
    if (inIframe || (token && !customizeMode)) {
      // Set page background to transparent for iframe embedding with !important to override any CSS
      document.documentElement.style.setProperty(
        "background-color",
        "transparent",
        "important"
      );
      document.body.style.setProperty(
        "background-color",
        "transparent",
        "important"
      );
      document.body.style.margin = "0";
      document.body.style.padding = "0";
    }
  }, [token, customizeMode]);

  // Keep the embedded widget fully visible by scaling down to fit the requested height
  useEffect(() => {
    if (!shouldRenderAsEmbedded) {
      setFitScale(1);
      return;
    }

    const card = cardRef.current;
    const outer = containerRef.current;
    if (!card || !outer) return;

    const availableHeight = Math.max(200, targetHeight);
    const availableWidth = Math.max(220, outer.clientWidth || window.innerWidth || 220);

    const measure = () => {
      const measuredHeight = card.scrollHeight || card.getBoundingClientRect().height;
      const measuredWidth = card.scrollWidth || card.getBoundingClientRect().width;
      if (!measuredHeight || !measuredWidth) return;

      // Leave a small cushion so the bottom edge never clips after scaling
      const heightScale = (availableHeight - 6) / measuredHeight;
      const widthScale = (availableWidth - 8) / measuredWidth;
      const next = Math.min(1, Math.max(0.4, Math.min(heightScale, widthScale)));

      if (Number.isFinite(next) && Math.abs(next - fitScale) > 0.01) {
        setFitScale(next);
      }
    };

    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [shouldRenderAsEmbedded, targetHeight, widgetTheme, widgetColor, widgetSize, widgetHeight, prayerTimes, currentIndex, fitScale]);

  // Generate widget token for customization mode
  const generateWidgetToken = useCallback(async () => {
    try {
      setGeneratingToken(true);

      if (!auth.currentUser) {
        // Auth not ready yet - will retry when auth state changes
        setGeneratingToken(false);
        return null;
      }

      const token = await auth.currentUser.getIdToken(true);
      const masjidId = userProfile?.masjidId || userProfile?.activeMasjidId;

      if (!masjidId) {
        console.error("No masjidId found in userProfile:", userProfile);
        setGeneratingToken(false);
        setError(
          "No masjid associated with your account. Please ensure you have an active masjid."
        );
        return null;
      }


      const response = await fetch(
        `${API_BASE_URL}/v1/widget-token/${masjidId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API response error:", response.status, errorText);
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (!data.ok) {
        throw new Error(data.error || "Failed to generate widget token");
      }

      setWidgetToken(data.token);
      setGeneratingToken(false);
      return data.token;
    } catch (err) {
      console.error("Error generating widget token:", err);
      setError(
        err.message || "Failed to generate widget token. Please try again."
      );
      setGeneratingToken(false);
      return null;
    }
  }, [userProfile, API_BASE_URL]);

  useEffect(() => {
    if (
      customizeMode &&
      !widgetToken &&
      !generatingToken &&
      user &&
      userProfile
    ) {
      // Wait for Firebase Auth to initialize before generating token
      const timer = setTimeout(() => {
        // Only generate if auth.currentUser is available
        if (auth.currentUser) {
          generateWidgetToken();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [
    customizeMode,
    widgetToken,
    user,
    userProfile,
    generatingToken,
    generateWidgetToken,
  ]);

  useEffect(() => {
    if (!token && !customizeMode) {
      setError("Invalid widget token");
      setLoading(false);
      return;
    }

    if (token) {
      loadWidgetData();
    }
  }, [token, customizeMode]);

  // Update theme when URL parameter changes (only for embedded widget, not customize mode)
  useEffect(() => {
    // Only apply theme class if not in customize mode (customize mode uses user's theme via ThemeContext)
    if (!customizeMode) {
      if (theme === "dark") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    }
  }, [theme, customizeMode]);

  // Auto-update widget when day changes (check every minute)
  useEffect(() => {
    if (!token || customizeMode || !masjidData) return;

    const masjidTz = masjidData?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    let lastCheckedDate = new Date().toLocaleDateString('en-CA', { timeZone: masjidTz });

    const checkForDayChange = () => {
      const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: masjidTz });
      if (currentDate !== lastCheckedDate) {
        lastCheckedDate = currentDate;
        // Reload data to get fresh prayer times starting from new day
        loadWidgetData();
      }
    };

    // Check every minute for day change
    const interval = setInterval(checkForDayChange, 60000);

    return () => clearInterval(interval);
  }, [token, customizeMode, masjidData]);

  const loadWidgetData = async () => {
    try {
      setLoading(true);

      // Use API endpoint instead of direct Firestore read
      const response = await fetch(`${API_URL}/v1/widget-data/${token}`);

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to load widget data");
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (!data.ok) {
        setError(data.error || "Failed to load widget data");
        setLoading(false);
        return;
      }

      setMasjidData(data.masjid);

      // Convert date strings to Date objects
      const times = data.prayerTimes.map((item) => ({
        date: item.date,
        data: item.data,
      }));

      // Filter to only show today and future days based on masjid's timezone
      const masjidTz = data.masjid?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const todayInMasjidTz = new Date().toLocaleDateString('en-CA', { timeZone: masjidTz }); // YYYY-MM-DD format

      const filteredTimes = times.filter((item) => {
        // Compare ISO strings directly to avoid UTC parsing shifts
        return item.date >= todayInMasjidTz;
      });

      // Use filtered times if we have any, otherwise fall back to all times
      // Limit to 7 days (current day + 6 more)
      const limitedTimes = (filteredTimes.length > 0 ? filteredTimes : times).slice(0, 7);
      setPrayerTimes(limitedTimes);
      setCurrentIndex(0); // Today is always first now

      setLoading(false);
    } catch (err) {
      console.error("Error loading widget data:", err);
      setError("Failed to load prayer times");
      setLoading(false);
    }
  };

  // Persist public calendar context whenever masjid data is available so navigation from the widget works reliably
  useEffect(() => {
    if (!masjidData) return;
    const id =
      masjidData.masjidId ||
      masjidData.id ||
      masjidData.key ||
      masjidData.masjid_id;
    if (id) {
      savePublicCalendarContext({ masjidId: id, masjid: masjidData });
    }
  }, [masjidData]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prayerTimes.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < prayerTimes.length - 1 ? prev + 1 : 0));
  };

  const isDark = theme === "dark";
  const currentDay = prayerTimes[currentIndex];

  const bgColor = isDark ? "#1a1a1a" : "#ffffff";
  const textColor = isDark ? "#ffffff" : "#0f172a";
  const mutedColor = isDark ? "#9ca3af" : "#64748b";
  const borderColor = isDark ? "#374151" : "#e5e7eb";

  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-").map(Number);
    const safeDate = new Date(Date.UTC(year, month - 1, day));
    return safeDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  // Icons aligned with TeleView
  const prayers = [
    { name: "Fajr", key: "fajr", IconComp: IoSunnyOutline },
    { name: "Sunrise", key: "sunrise", IconComp: IoPartlySunnyOutline },
    { name: "Dhuhr", key: "duhr", IconComp: IoSunny },
    { name: "Asr", key: "asr", IconComp: IoPartlySunny },
    { name: "Maghrib", key: "maghrib", IconComp: IoMoonOutline },
    { name: "Isha", key: "isha", IconComp: IoMoon },
  ];

  // Render widget content
  const renderWidget = () => {
    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            gap: "16px",
          }}
        >
          <Loader2
            className="animate-spin"
            size={32}
            style={{ color: accentColor }}
          />
          <p
            style={{
              color: mutedColor,
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Loading prayer times...
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            padding: "20px",
            textAlign: "center",
            gap: "12px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: isDark
                ? "rgba(239, 68, 68, 0.1)"
                : "rgba(239, 68, 68, 0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: "24px" }}>⚠️</span>
          </div>
          <p
            style={{
              color: "#ef4444",
              fontSize: "16px",
              fontWeight: "600",
              margin: 0,
            }}
          >
            {error}
          </p>
          <p
            style={{
              color: mutedColor,
              fontSize: "14px",
              margin: 0,
            }}
          >
            Please check your widget token and try again.
          </p>
        </div>
      );
    }

    if (!currentDay) {
      return null;
    }

    // Get the masjid's timezone (fallback to local if not available)
    const masjidTimezone = masjidData?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Helper: get current date/time in masjid's timezone
    const getNowInMasjidTimezone = () => {
      const now = new Date();
      // Get the date/time string in masjid's timezone
      const options = {
        timeZone: masjidTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      };
      const formatter = new Intl.DateTimeFormat('en-US', options);
      const parts = formatter.formatToParts(now);
      const getPart = (type) => parts.find(p => p.type === type)?.value;

      return {
        year: parseInt(getPart('year')),
        month: parseInt(getPart('month')) - 1, // JS months are 0-indexed
        day: parseInt(getPart('day')),
        hour: parseInt(getPart('hour')),
        minute: parseInt(getPart('minute')),
        second: parseInt(getPart('second')),
      };
    };

    const masjidNow = getNowInMasjidTimezone();

    // Helper: convert a time string (HH:MM or H:MM AM/PM) to minutes since midnight for comparison
    const timeStringToMinutes = (timeStr, prayerKey = null) => {
      if (!timeStr) return null;
      const str = String(timeStr).trim();

      // Check if time has AM/PM
      const hasAMPM = /[AaPp][Mm]/.test(str);

      if (hasAMPM) {
        // Parse 12-hour format like "2:12 PM" or "6:17 AM"
        const match = str.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
        if (!match) return null;
        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const period = match[3].toUpperCase();

        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;

        return hours * 60 + minutes;
      } else {
        // Parse 24-hour format like "14:12" or use prayer-specific logic for ambiguous times
        const [h, m] = str.split(":").map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return null;

        // If hour is <= 12 and we have prayer context, use prayer-specific AM/PM rules
        if (h <= 12 && prayerKey) {
          let hours = h;
          if (prayerKey === 'fajr' || prayerKey === 'sunrise') {
            // Fajr and Sunrise are always AM - hours stay as is (or convert 12 to 0)
            if (hours === 12) hours = 0;
          } else if (prayerKey === 'duhr' || prayerKey === 'jummah') {
            // Dhuhr/Jummah: PM unless 10-11 AM
            if (hours >= 10 && hours < 12) {
              // Keep as AM
            } else if (hours === 12) {
              // 12 PM stays as 12
            } else {
              // Add 12 for PM
              hours += 12;
            }
          } else if (prayerKey === 'asr' || prayerKey === 'maghrib' || prayerKey === 'isha') {
            // Asr, Maghrib, Isha are always PM
            if (hours !== 12) hours += 12;
          }
          return hours * 60 + m;
        }

        return h * 60 + m;
      }
    };

    // Current time in minutes since midnight (in masjid's timezone)
    const nowMinutes = masjidNow.hour * 60 + masjidNow.minute;

    // Check if currentDay matches today in masjid's timezone
    const isSameDayInMasjidTz = () => {
      const [y, m, d] = (currentDay.date || "").split("-").map(Number);
      return y === masjidNow.year && m - 1 === masjidNow.month && d === masjidNow.day;
    };

    // Determine current active prayer window: highlight the next upcoming iqama, or current if now is between adhan and iqama
    const getCurrentPrayerKey = () => {
      // Build ordered list with comparable times
      const order = ["fajr", "sunrise", "duhr", "asr", "maghrib", "isha"];
      for (let i = 0; i < order.length; i++) {
        const key = order[i];
        const pdata = currentDay.data[key];
        if (!pdata) continue;
        if (key === "sunrise") {
          const t = timeStringToMinutes(pdata, key);
          if (t !== null && nowMinutes < t) return key;
          continue;
        }
        const adhanMinutes = timeStringToMinutes(pdata?.adhan, key);
        const iqamaMinutes = timeStringToMinutes(pdata?.iqama, key);
        // If now is between adhan and iqama, it's current; else if iqama is in future, it's next
        if (adhanMinutes !== null && iqamaMinutes !== null && nowMinutes >= adhanMinutes && nowMinutes <= iqamaMinutes) return key;
        if (iqamaMinutes !== null && nowMinutes < iqamaMinutes) return key;
      }
      // If all passed, highlight last prayer (isha) as fallback
      return "isha";
    };
    // Only highlight for today's date (in masjid's timezone)
    const activePrayerKey = isSameDayInMasjidTz()
      ? getCurrentPrayerKey()
      : null;

    const jummahTimes = currentDay?.data?.jummah
      ? [
          currentDay.data.jummah["1"],
          currentDay.data.jummah["2"],
          currentDay.data.jummah["3"],
        ].filter(Boolean)
      : [];

    const khutbahTimes = currentDay?.data?.khutbah
      ? [
          currentDay.data.khutbah["1"],
          currentDay.data.khutbah["2"],
          currentDay.data.khutbah["3"],
        ].filter(Boolean)
      : [];

    // Calculate dimensions and scaling based on size and target height
    const getDimensions = () => {
      const hasJummah = jummahTimes.length > 0;

      // Base sizes at scale 1 - these represent the minimum comfortable sizes
      // The widget will scale up/down to fill the target height while maintaining 9:16 aspect ratio
      const header = 70;
      const date = 50;
      const tableHeader = 40;
      const tableRows = 6 * 46;
      const navigation = 50;
      const calendarButton = 55;
      const footer = 45;
      const tablePadding = 28;
      const jummah = hasJummah ? 140 : 0;

      const baseTotal =
        header +
        date +
        tableHeader +
        tableRows +
        tablePadding +
        navigation +
        calendarButton +
        footer +
        jummah;

      const usableHeight = Math.max(200, targetHeight);
      // Scale to fit target height exactly
      const scale = Math.max(0.2, usableHeight / baseTotal);

      // Width is always 9/16 of height for 9:16 aspect ratio
      const widthPx = `${Math.round((targetHeight * 9) / 16)}px`;

      // Font sizes scale with the widget
      const fontSize = {
        title: `${26 * scale}px`,
        subtitle: `${15 * scale}px`,
        date: `${17 * scale}px`,
        tableHeader: `${15 * scale}px`,
        tableCell: `${18 * scale}px`,
        jummah: `${20 * scale}px`,
        footer: `${14 * scale}px`,
      };
      // Spacing scales with the widget
      const spacing = {
        padding: `${14 * scale}px`,
        paddingSmall: `${10 * scale}px`,
        gap: `${6 * scale}px`,
      };

      return {
        width: widthPx,
        height: `${Math.max(200, targetHeight)}px`,
        scale,
        fontSize,
        spacing,
      };
    };

    const dimensions = getDimensions();

    return (
      <div
        ref={containerRef}
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          backgroundColor: shouldRenderAsEmbedded ? "transparent" : undefined,
          color: textColor,
          minHeight: shouldRenderAsEmbedded
            ? `${Math.max(200, targetHeight)}px`
            : "calc(100vh - 4rem)",
          width: "100%",
          height: shouldRenderAsEmbedded ? `${Math.max(200, targetHeight)}px` : "auto",
          maxHeight: shouldRenderAsEmbedded ? `${Math.max(200, targetHeight)}px` : undefined,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: shouldRenderAsEmbedded ? "flex-start" : "center",
          padding: shouldRenderAsEmbedded ? "0" : "2rem",
          boxSizing: "border-box",
          overflow: shouldRenderAsEmbedded ? "visible" : "visible",
        }}
      >
        <div
          style={{
            transform: shouldRenderAsEmbedded ? `scale(${fitScale})` : "none",
            transformOrigin: "top left",
            width: shouldRenderAsEmbedded ? dimensions.width : "100%",
            display: "flex",
            justifyContent: shouldRenderAsEmbedded ? "flex-start" : "center",
          }}
        >
          <div
            ref={cardRef}
            style={{
              backgroundColor: bgColor,
              maxWidth: shouldRenderAsEmbedded ? dimensions.width : "500px",
              width: shouldRenderAsEmbedded ? dimensions.width : "100%",
              height: "auto",
              minHeight: "auto",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: shouldRenderAsEmbedded
                ? "none"
                : isDark
                ? "0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05)"
                : "0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.12)"
                : "1px solid rgba(0, 0, 0, 0.1)",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              transition: "box-shadow 0.3s ease, border-color 0.3s ease",
            }}
          >
          {/* Header */}
          <div
            style={{
              textAlign: "center",
              padding: `${16 * (dimensions.scale || 1)}px ${14 * (dimensions.scale || 1)}px`,
              borderBottom: `2px solid ${borderColor}`,
              background: isDark
                ? "linear-gradient(to bottom, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01))"
                : "linear-gradient(to bottom, rgba(0, 0, 0, 0.03), rgba(0, 0, 0, 0.01))",
              flexShrink: 0,
              position: "relative",
            }}
          >
            <h2
              style={{
                margin: `0 0 ${8 * (dimensions.scale || 1)}px 0`,
                fontSize: dimensions.fontSize?.title || "26px",
                fontWeight: "700",
                color: accentColor,
                letterSpacing: "1px",
                lineHeight: "1.2",
                textShadow: isDark
                  ? `0 1px 2px ${hexToRgba(accentColor, 0.2)}`
                  : "none",
              }}
            >
              PRAYER TIMES
            </h2>
            {masjidData?.name && (
              <p
                style={{
                  margin: "0",
                  fontSize: dimensions.fontSize?.subtitle || "14px",
                  color: mutedColor,
                  fontWeight: "500",
                  lineHeight: "1.4",
                  letterSpacing: "0.3px",
                }}
              >
                {masjidData.name}
              </p>
            )}
          </div>

          {/* Date Display */}
          <div
            style={{
              textAlign: "center",
              padding: `${14 * (dimensions.scale || 1)}px ${12 * (dimensions.scale || 1)}px`,
              borderBottom: `1px solid ${borderColor}`,
              background: isDark
                ? "rgba(255, 255, 255, 0.02)"
                : "rgba(0, 0, 0, 0.015)",
              flexShrink: 0,
            }}
          >
            <p
              style={{
                margin: `0 0 ${6 * (dimensions.scale || 1)}px 0`,
                fontSize: dimensions.fontSize?.date || "17px",
                fontWeight: "600",
                lineHeight: "1.4",
                color: textColor,
                letterSpacing: "0.2px",
              }}
            >
              {formatDate(currentDay.date)}
            </p>
            {currentDay.data.islamicDate && (
              <p
                style={{
                  margin: "0",
                  fontSize: dimensions.fontSize?.subtitle || "14px",
                  color: mutedColor,
                  lineHeight: "1.4",
                  fontStyle: "italic",
                }}
              >
                {currentDay.data.islamicDate}
              </p>
            )}
          </div>

          {/* Prayer Times Table */}
          <div
            style={{
              paddingTop: `${12 * (dimensions.scale || 1)}px`,
              paddingLeft: `${12 * (dimensions.scale || 1)}px`,
              paddingRight: `${12 * (dimensions.scale || 1)}px`,
              paddingBottom:
                jummahTimes.length > 0
                  ? `${12 * (dimensions.scale || 1)}px`
                  : `${6 * (dimensions.scale || 1)}px`,
              flex: "0 0 auto",
              overflow: "visible",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr style={{ borderBottom: `2px solid ${borderColor}` }}>
                  <th
                    style={{
                      padding: `${10 * (dimensions.scale || 1)}px ${
                        6 * (dimensions.scale || 1)
                      }px`,
                      textAlign: "left",
                      fontSize: dimensions.fontSize?.tableHeader || "15px",
                      fontWeight: "700",
                      color: mutedColor,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Prayer
                  </th>
                  <th
                    style={{
                      padding: `${10 * (dimensions.scale || 1)}px ${
                        6 * (dimensions.scale || 1)
                      }px`,
                      textAlign: "center",
                      fontSize: dimensions.fontSize?.tableHeader || "15px",
                      fontWeight: "700",
                      color: mutedColor,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Adhan
                  </th>
                  <th
                    style={{
                      padding: `${10 * (dimensions.scale || 1)}px ${
                        6 * (dimensions.scale || 1)
                      }px`,
                      textAlign: "center",
                      fontSize: dimensions.fontSize?.tableHeader || "15px",
                      fontWeight: "700",
                      color: mutedColor,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Iqamah
                  </th>
                </tr>
              </thead>
              <tbody>
                {prayers.map((prayer) => {
                  const prayerData = currentDay.data[prayer.key];
                  const isSpecial = prayer.key === "sunrise";

                  return (
                    <tr
                      key={prayer.key}
                      style={{
                        borderBottom: `1px solid ${borderColor}`,
                        background:
                          activePrayerKey && activePrayerKey === prayer.key
                            ? isDark
                              ? hexToRgba(accentColor, 0.15)
                              : hexToRgba(accentColor, 0.1)
                            : "transparent",
                        transition:
                          "background-color 0.2s ease, transform 0.1s ease",
                        transform: "scale(1)",
                      }}
                      onMouseEnter={(e) => {
                        if (
                          !(activePrayerKey && activePrayerKey === prayer.key)
                        ) {
                          e.currentTarget.style.backgroundColor = isDark
                            ? "rgba(255, 255, 255, 0.03)"
                            : "rgba(0, 0, 0, 0.02)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (
                          !(activePrayerKey && activePrayerKey === prayer.key)
                        ) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      <td
                        style={{
                          padding: `${12 * (dimensions.scale || 1)}px ${
                            8 * (dimensions.scale || 1)
                          }px`,
                          fontSize: dimensions.fontSize?.tableCell || "18px",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: `${8 * (dimensions.scale || 1)}px`,
                        }}
                      >
                        {prayer.IconComp && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              color:
                                activePrayerKey &&
                                activePrayerKey === prayer.key
                                  ? accentColor
                                  : mutedColor,
                              transition: "color 0.2s ease",
                            }}
                          >
                            <prayer.IconComp
                              size={Math.round(22 * (dimensions.scale || 1))}
                            />
                          </span>
                        )}
                        <span
                          style={{
                            fontWeight:
                              activePrayerKey && activePrayerKey === prayer.key
                                ? 700
                                : 500,
                            color:
                              activePrayerKey && activePrayerKey === prayer.key
                                ? accentColor
                                : textColor,
                            transition:
                              "color 0.2s ease, font-weight 0.2s ease",
                          }}
                        >
                          {prayer.name}
                        </span>
                      </td>
                      <>
                        <td
                          style={{
                            padding: `${12 * (dimensions.scale || 1)}px ${
                              8 * (dimensions.scale || 1)
                            }px`,
                            textAlign: "center",
                            fontSize: dimensions.fontSize?.tableCell || "18px",
                            color:
                              activePrayerKey && activePrayerKey === prayer.key
                                ? accentColor
                                : mutedColor,
                            lineHeight: "1.3",
                            fontWeight: "500",
                            transition: "color 0.2s ease",
                          }}
                        >
                          {isSpecial
                            ? formatTimeWithAMPM(prayerData, prayer.key)
                            : formatTimeWithAMPM(prayerData?.adhan, prayer.key)}
                        </td>
                        <td
                          style={{
                            padding: `${12 * (dimensions.scale || 1)}px ${
                              8 * (dimensions.scale || 1)
                            }px`,
                            textAlign: "center",
                            fontSize: dimensions.fontSize?.tableCell || "18px",
                            fontWeight:
                              activePrayerKey && activePrayerKey === prayer.key
                                ? 800
                                : 700,
                            color: isSpecial ? mutedColor : accentColor,
                            lineHeight: "1.3",
                            transition:
                              "color 0.2s ease, font-weight 0.2s ease",
                          }}
                        >
                          {isSpecial
                            ? "—"
                            : formatTimeWithAMPM(prayerData?.iqama, prayer.key)}
                        </td>
                      </>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Jummah Times */}
            {jummahTimes.length > 0 && (
              <div
                style={{
                  marginTop: `${12 * (dimensions.scale || 1)}px`,
                  padding: `${12 * (dimensions.scale || 1)}px ${
                    dimensions.spacing?.paddingSmall || "10px"
                  }`,
                  marginBottom: 0,
                  backgroundColor: isDark
                    ? hexToRgba(accentColor, 0.12)
                    : hexToRgba(accentColor, 0.1),
                  borderRadius: `${10 * (dimensions.scale || 1)}px`,
                  border: `1.5px solid ${
                    isDark
                      ? hexToRgba(accentColor, 0.25)
                      : hexToRgba(accentColor, 0.2)
                  }`,
                  textAlign: "center",
                  boxShadow: isDark
                    ? `0 2px 8px ${hexToRgba(accentColor, 0.1)}`
                    : `0 2px 8px ${hexToRgba(accentColor, 0.08)}`,
                }}
              >
                <p
                  style={{
                    margin: `0 0 ${8 * (dimensions.scale || 1)}px 0`,
                    fontSize: dimensions.fontSize?.tableHeader || "15px",
                    fontWeight: "700",
                    color: mutedColor,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                  }}
                >
                  {jummahTimes.length === 1
                            ? "Jummah"
                            : `Jummah Times`}
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: `${8 * (dimensions.scale || 1)}px`,
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {jummahTimes.map((time, idx) => {
                    const khutbahTime = khutbahTimes[idx];
                    return (
                      <div
                        key={idx}
                        style={{
                          flex: "1 1 auto",
                          minWidth: `${100 * (dimensions.scale || 1)}px`,
                          padding: `${6 * (dimensions.scale || 1)}px`,
                          borderRadius: `${8 * (dimensions.scale || 1)}px`,
                          backgroundColor: isDark
                            ? hexToRgba(accentColor, 0.08)
                            : hexToRgba(accentColor, 0.05),
                          transition:
                            "transform 0.2s ease, background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.backgroundColor = isDark
                            ? hexToRgba(accentColor, 0.15)
                            : hexToRgba(accentColor, 0.1);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.backgroundColor = isDark
                            ? hexToRgba(accentColor, 0.08)
                            : hexToRgba(accentColor, 0.05);
                        }}
                      >
                        <div
                          style={{
                            fontSize: dimensions.fontSize?.subtitle || "12px",
                            color: mutedColor,
                            lineHeight: "1.4",
                            fontWeight: "500",
                            marginBottom: `${2 * (dimensions.scale || 1)}px`,
                          }}
                        >
                          {jummahTimes.length === 1
                            ? ""
                            : `Jummah ${idx + 1}`}
                        </div>
                        {/* Khutbah time if exists */}
                        {khutbahTime && (
                          <div
                            style={{
                              fontSize: `${14 * (dimensions.scale || 1)}px`,
                              fontWeight: "600",
                              color: mutedColor,
                              lineHeight: "1.4",
                              marginBottom: `${2 * (dimensions.scale || 1)}px`,
                            }}
                          >
                            <span style={{ fontSize: `${11 * (dimensions.scale || 1)}px`, marginRight: "4px" }}>Khutbah:</span>
                            {formatTimeWithAMPM(khutbahTime, "jummah")}
                          </div>
                        )}
                        {/* Prayer time */}
                        <div
                          style={{
                            fontSize: dimensions.fontSize?.jummah || "18px",
                            fontWeight: "700",
                            color: accentColor,
                            lineHeight: "1.4",
                          }}
                        >
                          {khutbahTime && (
                            <span style={{ fontSize: `${11 * (dimensions.scale || 1)}px`, marginRight: "4px", color: mutedColor, fontWeight: "500" }}>Prayer:</span>
                          )}
                          {formatTimeWithAMPM(time, "jummah")}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: `${8 * (dimensions.scale || 1)}px`,
              padding: `${12 * (dimensions.scale || 1)}px ${
                12 * (dimensions.scale || 1)
              }px`,
              flexShrink: 0,
            }}
          >
            <button
              onClick={handlePrevious}
              style={{
                padding: `${8 * (dimensions.scale || 1)}px`,
                border: `1.5px solid ${borderColor}`,
                borderRadius: `${6 * (dimensions.scale || 1)}px`,
                backgroundColor: "transparent",
                color: textColor,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                boxShadow: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark
                  ? "#374151"
                  : "#f3f4f6";
                e.currentTarget.style.borderColor = accentColor;
                e.currentTarget.style.color = accentColor;
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.boxShadow = `0 2px 8px ${hexToRgba(
                  accentColor,
                  0.2
                )}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.borderColor = borderColor;
                e.currentTarget.style.color = textColor;
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <ChevronLeft size={Math.round(20 * (dimensions.scale || 1))} />
            </button>

            <div
              style={{
                display: "flex",
                gap: `${6 * (dimensions.scale || 1)}px`,
                padding: `0 ${10 * (dimensions.scale || 1)}px`,
              }}
            >
              {prayerTimes.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  style={{
                    width: `${10 * (dimensions.scale || 1)}px`,
                    height: `${10 * (dimensions.scale || 1)}px`,
                    borderRadius: "50%",
                    border: `2px solid ${
                      idx === currentIndex ? accentColor : borderColor
                    }`,
                    backgroundColor:
                      idx === currentIndex ? accentColor : "transparent",
                    cursor: "pointer",
                    padding: 0,
                    transition: "all 0.2s ease",
                    transform: idx === currentIndex ? "scale(1.2)" : "scale(1)",
                  }}
                  onMouseEnter={(e) => {
                    if (idx !== currentIndex) {
                      e.currentTarget.style.borderColor = accentColor;
                      e.currentTarget.style.backgroundColor = hexToRgba(
                        accentColor,
                        0.3
                      );
                      e.currentTarget.style.transform = "scale(1.15)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (idx !== currentIndex) {
                      e.currentTarget.style.borderColor = borderColor;
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.transform = "scale(1)";
                    }
                  }}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              style={{
                padding: `${8 * (dimensions.scale || 1)}px`,
                border: `1.5px solid ${borderColor}`,
                borderRadius: `${6 * (dimensions.scale || 1)}px`,
                backgroundColor: "transparent",
                color: textColor,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                boxShadow: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark
                  ? "#374151"
                  : "#f3f4f6";
                e.currentTarget.style.borderColor = accentColor;
                e.currentTarget.style.color = accentColor;
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.boxShadow = `0 2px 8px ${hexToRgba(
                  accentColor,
                  0.2
                )}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.borderColor = borderColor;
                e.currentTarget.style.color = textColor;
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <ChevronRight size={Math.round(20 * (dimensions.scale || 1))} />
            </button>
          </div>

          {/* View Monthly Calendar Link */}
          <div
            style={{
              textAlign: "center",
              padding: `${14 * (dimensions.scale || 1)}px ${
                12 * (dimensions.scale || 1)
              }px`,
              flexShrink: 0,
            }}
          >
            <a
              href={token ? `/masjids/calendar?token=${encodeURIComponent(token)}` : "/masjids/calendar"}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: "none",
                color: textColor,
                display: "inline-block",
                transition: "transform 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
              onClick={() => {
                const id =
                  masjidData?.masjidId ||
                  masjidData?.id ||
                  masjidData?.key ||
                  masjidData?.masjid_id;
                if (id) {
                  savePublicCalendarContext({ masjidId: id, masjid: masjidData });
                  console.info("Widget calendar: saved context", { id, title: masjidData?.title });
                } else {
                  console.error("Widget calendar: missing masjid id for context", masjidData);
                }
              }}
            >
              <div
                style={{
                  fontSize: `${16 * (dimensions.scale || 1)}px`,
                  fontWeight: "600",
                  color: textColor,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: `${6 * (dimensions.scale || 1)}px`,
                  lineHeight: "1.3",
                  transition: "color 0.2s ease",
                }}
              >
                View Monthly Calendar
              </div>
              <div
                style={{
                  width: "auto",
                  minWidth: "140px",
                  height: `3px`,
                  backgroundColor: accentColor,
                  margin: "0 auto",
                  borderRadius: "2px",
                  transition: "width 0.3s ease, background-color 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.width = "100%";
                  e.currentTarget.style.backgroundColor = accentColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.width = "auto";
                  e.currentTarget.style.backgroundColor = accentColor;
                }}
              />
            </a>
          </div>

          {/* Footer */}
          <div
            style={{
              textAlign: "center",
              padding: `${10 * (dimensions.scale || 1)}px ${
                12 * (dimensions.scale || 1)
              }px ${14 * (dimensions.scale || 1)}px`,
              fontSize: dimensions.fontSize?.footer || "14px",
              color: mutedColor,
              background: isDark
                ? "rgba(255, 255, 255, 0.02)"
                : "rgba(0, 0, 0, 0.015)",
              flexShrink: 0,
              borderTop: `1px solid ${borderColor}`,
            }}
          >
            <a
              href="https://muslimplus.org"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: "none",
                color: "inherit",
                transition: "color 0.2s ease",
                display: "inline-block",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = accentColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "inherit";
              }}
            >
              Powered by{" "}
              <strong
                style={{
                  color: accentColor,
                  fontWeight: "700",
                  transition: "color 0.2s ease",
                }}
              >
                Muslim+
              </strong>
            </a>
          </div>
          </div>
        </div>
      </div>
    );
  };

  // Customization helper functions
  const getWidgetUrl = useCallback(() => {
    if (!widgetToken) return "";
    const colorParam = widgetColor.startsWith("#")
      ? widgetColor.substring(1)
      : widgetColor;
    const height = resolveHeight(widgetSize, widgetHeight);
    const heightQuery = height ? `&height=${height}` : "";
    return `${window.location.origin}/widget?token=${widgetToken}&theme=${widgetTheme}&color=${colorParam}&size=${widgetSize}${heightQuery}`;
  }, [widgetToken, widgetTheme, widgetColor, widgetSize, widgetHeight]);

  const getSizeStyles = () => {
    const height = resolveHeight(widgetSize, widgetHeight);

    const width = Math.max(220, Math.round((height * 9) / 16));

    return {
      maxWidth: `${width}px`,
      height: `${height}px`,
    };
  };

  const getEmbedCode = (widgetUrl) => {
    if (!widgetUrl) return "";
    const sizeStyles = getSizeStyles();
    const minHeight = sizeStyles.minHeight
      ? ` min-height: ${sizeStyles.minHeight};`
      : "";
    return `<iframe src="${widgetUrl}" frameborder="0" scrolling="no" style="border: none; width: 100%; max-width: ${sizeStyles.maxWidth}; height: ${sizeStyles.height};${minHeight} display: block; margin: 0 auto;"></iframe>`;
  };

  const handleSizeSelect = (size) => {
    setWidgetSize(size);
    if (size !== "custom") {
      setWidgetHeight(resolveHeight(size, widgetHeight));
    }
  };

  const copyWidgetCode = async () => {
    try {
      const widgetUrl = getWidgetUrl();
      if (!widgetUrl) return;

      if (widgetSize === "custom") {
        const num = Number(widgetHeight);
        if (!Number.isFinite(num) || num < 200) {
          window.alert("Please enter a valid height (200px or greater) before copying.");
          return;
        }
      }

      const code = getEmbedCode(widgetUrl);

      await navigator.clipboard.writeText(code);
      setCopiedWidget(true);
      setTimeout(() => setCopiedWidget(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Render customization interface
  const renderCustomization = () => {
    // Check if user is authenticated and has a masjid
    const hasMasjid = userProfile?.masjidId || userProfile?.activeMasjidId;

    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="space-y-6">
          {/* Header */}
          <div className="pb-4 border-b">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Code className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">Prayer Times Widget</h1>
                <p className="text-sm text-muted-foreground mt-1.5">
                  Customize your widget and copy the embed code to add it to
                  your website. No technical knowledge required!
                </p>
              </div>
            </div>
          </div>

          {/* Authentication/Masjid Warning */}
          {!user && (
            <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-4">
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                Please log in to generate a widget token.
              </p>
            </div>
          )}
          {user && !hasMasjid && (
            <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-4">
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                No masjid associated with your account. Please ensure you have
                an active masjid to generate a widget token.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Customization */}
            <div className="space-y-6">
              {/* Customization Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b">
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                    Customization
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Theme */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Theme</Label>
                    <div className="flex gap-2">
                      <Button
                        variant={
                          widgetTheme === "light" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setWidgetTheme("light")}
                        className="flex-1"
                      >
                        <Sun className="mr-2 h-4 w-4" />
                        Light
                      </Button>
                      <Button
                        variant={widgetTheme === "dark" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setWidgetTheme("dark")}
                        className="flex-1"
                      >
                        <Moon className="mr-2 h-4 w-4" />
                        Dark
                      </Button>
                    </div>
                  </div>

                  {/* Accent Color */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="widgetColor"
                      className="text-sm font-medium"
                    >
                      Accent Color
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="widgetColor"
                        type="color"
                        value={widgetColor}
                        onChange={(e) => setWidgetColor(e.target.value)}
                        className="h-10 w-16 cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={widgetColor}
                        onChange={(e) => setWidgetColor(e.target.value)}
                        placeholder="#bf9a2f"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                  </div>

                  {/* Size */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Size</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={widgetSize === "small" ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleSizeSelect("small")}
                        className="text-xs"
                      >
                        Small (250px)
                      </Button>
                      <Button
                        variant={
                          widgetSize === "medium" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handleSizeSelect("medium")}
                        className="text-xs"
                      >
                        Medium (400px)
                      </Button>
                      <Button
                        variant={widgetSize === "large" ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleSizeSelect("large")}
                        className="text-xs"
                      >
                        Large (500px)
                      </Button>
                      <Button
                        variant={
                          widgetSize === "custom" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handleSizeSelect("custom")}
                        className="text-xs"
                      >
                        Custom
                      </Button>
                    </div>
                    {widgetSize === "custom" && (
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground" htmlFor="customHeight">
                          Custom height (px)
                        </Label>
                        <Input
                          id="customHeight"
                          type="text"
                          value={widgetHeight}
                          onChange={(e) => setWidgetHeight(e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Embed Code Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between pb-2 border-b">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm font-medium">Embed Code</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={copyWidgetCode}
                      disabled={!widgetToken}
                      className="gap-2"
                    >
                      {copiedWidget ? (
                        <>
                          <Check className="h-4 w-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy Code
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <div className="rounded-lg bg-muted/50 border p-4 relative">
                  {!widgetToken ? (
                    <div className="flex flex-col items-center justify-center py-8 gap-3">
                      {generatingToken ? (
                        <>
                          <Loader2 className="h-6 w-6 animate-spin text-primary" />
                          <p className="text-sm text-muted-foreground">
                            Generating widget token...
                          </p>
                        </>
                      ) : error ? (
                        <>
                          <p className="text-sm text-destructive font-medium">
                            {error}
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setError(null);
                              generateWidgetToken();
                            }}
                            className="mt-2"
                          >
                            Retry
                          </Button>
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Waiting to generate widget token...
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <code className="text-xs break-all whitespace-pre-wrap font-mono block overflow-x-auto">
                        {(() => {
                          const widgetUrl = getWidgetUrl();
                            return getEmbedCode(widgetUrl);
                        })()}
                      </code>
                    </>
                  )}
                </div>
                <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span>💡</span>
                  <span>
                    Simply copy the code above and paste it into your website's
                    HTML where you want the widget to appear.
                  </span>
                </p>
              </div>
            </div>

            {/* Right Column - Preview */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 pb-2 border-b">
                <Eye className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Live Preview</Label>
                {widgetToken && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const widgetUrl = getWidgetUrl();
                      if (widgetUrl) {
                        window.open(widgetUrl, "_blank", "noopener,noreferrer");
                      }
                    }}
                    className="gap-2 ml-auto"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open in New Tab
                  </Button>
                )}
              </div>
              <div
                className="rounded-lg border bg-muted/30 overflow-hidden"
                style={{
                  height: "560px",
                  minHeight: "420px",
                  maxHeight: "720px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {widgetToken ? (
                  <PreviewIframe
                    key={`${widgetToken}-${widgetTheme}-${widgetColor}-${widgetSize}-${widgetHeight}`}
                    src={getWidgetUrl()}
                    size={widgetSize}
                    targetHeight={resolveHeight(widgetSize, widgetHeight)}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    {generatingToken ? (
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">
                          Generating widget token...
                        </p>
                      </div>
                    ) : (
                      <div className="text-center space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Widget token will be generated automatically
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Preview will appear here once ready
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const widgetContent = renderWidget();

  // If in customization mode, show customization interface
  if (customizeMode) {
    return <AppLayout>{renderCustomization()}</AppLayout>;
  }

  // If embedded in iframe OR viewing widget directly (has token), return widget without AppLayout
  // This ensures "Open in New Tab" shows only the widget, not the website nav
  if (isEmbedded || (token && !customizeMode)) {
    return widgetContent;
  }

  // If viewing directly without token (shouldn't happen normally), wrap with AppLayout
  return <AppLayout>{widgetContent}</AppLayout>;
}
